import * as ents from '@attain-apps/entities'

import type { TypeParams } from './type-params'

/**
 * Similar to {@link ents.core.common.Attribute}, but with a
 * {@link ents.core.common.Attribute.ValueExtractor} so that these attributes
 * can be used to retrieve values from the entities
 */
export type Attribute<TTypeParams extends TypeParams.Constraint> = Readonly<
  ents.core.common.Attribute &
    ents.core.common.Attribute.ValueExtractor<TTypeParams['entity']> & {
      /**
       * @default false
       */
      isSortable?: boolean
    }
>
export namespace Attribute {
  /**
   * Produces an {@link Attribute} that corresponds to a key on the entity type
   */
  export function fromKey<TTypeParams extends TypeParams.Constraint>(
    key: Extract<keyof TTypeParams['entity'], string>,
    info: Readonly<Omit<Attribute<TTypeParams>, 'id' | 'getValueFrom'>>,
  ): Attribute<TTypeParams> {
    return {
      ...info,
      getValueFrom: entity => entity[key],
      id: key,
    }
  }

  /**
   * Similar to {@link ents.core.common.Attribute.inferFrom}, but produces ec.{@link Attribute}
   */
  export function inferFrom<TTypeParams extends TypeParams.Constraint>(
    entityOrEntities: Maybe.ReadonlyArray<TTypeParams['entity']>,
    options?: Readonly<ents.core.common.Attribute.inferFrom.Options>,
  ): readonly Attribute<TTypeParams>[] {
    return ents.core.common.Attribute.inferFrom(entityOrEntities, options).map(
      attribute => ({
        ...attribute,
        getValueFrom: entity => entity[attribute.id],
      }),
    )
  }
}
